import { TextField } from '@mui/material';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { isNumber } from 'lodash';
import styles from './index.module.css';
import { checkIsPositiveInteger } from '../../../../utils/skill';
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { inputBlackList } from '../../../../constants/inputBlackList';
import globalStyles from '../../../../globalStyles';
import { dashboardDes } from '../../language';
import { HexagonSkillView } from '../GoalCard/HexagonSkillView';
import React from 'react';

export const InputWithStyle = styled(TextField)(props => ({
  '& .MuiInputBase-root': {
    width: props.width,
    lineHeight: '1.1876em'
  },
  '& .MuiOutlinedInput-root': {
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none'
    },
    '& .MuiOutlinedInput-input': {
      border: `1px solid ${globalStyles.grey21}`,
      borderRadius: '6px',
      padding: '10px',
      height: props.height,
      '&::-webkit-input-placeholder': {
        textIndent: '2px',
        color: globalStyles.goalCreatedPlaceholder,
        fontSize: '16px',
        fontStyle: 'italic'
      }
    }
  },
  '& .MuiInputBase-multiline': {
    padding: '10px 0 0 10px'
  },
  '& .Mui-disabled': {
    color: globalStyles.black3
  }
}));

const GoalInformationForm = forwardRef(
  (
    {
      selectedSkill,
      disabled,
      goal,
      justDisplay,
      setIsDisplaySkillErrMsg,
      isMyOwnGoal
    },
    ref
  ) => {
    const { name, practiceLevel } = selectedSkill || {
      name: goal?.name,
      practiceLevel: goal?.practiceLevel
    };
    const [period, setPeriod] = useState(
      isNumber(goal?.period) ? goal.period : ''
    );
    const [description, setDescription] = useState(goal?.description || '');
    const [isPeriodError, setPeriodIsError] = useState(false);
    const [periodErrorMessage, setPeriodErrorMessage] = useState('');
    const [isDescError, setDescIsError] = useState(false);
    const [descErrorMessage, setDescErrorMessage] = useState('');
    useImperativeHandle(ref, () => {
      return {
        getValue() {
          return {
            period,
            description
          };
        },
        setValue({ period, description }) {
          setPeriod(isNumber(period) ? period : '');
          setDescription(description || '');
        },
        isInValidate() {
          const isPeriodHasError = !checkIsPositiveInteger(period);
          const isDescriptionHasError = !description.trim();
          let errorMessages = [];
          if (isPeriodHasError) {
            setPeriodIsError(true);
            errorMessages.push('duration');
            setPeriodErrorMessage(
              dashboardDes.myPlanCardDes.goalInformation.noPeriodErrorMessage
            );
          }
          if (isDescriptionHasError) {
            setDescIsError(true);
            errorMessages.push('description');
            setDescErrorMessage(
              dashboardDes.myPlanCardDes.goalInformation.noDescErrorMessage
            );
          }
          return errorMessages.length > 0 || isPeriodError || isDescError;
        }
      };
    });
    const validatePeriod = month => {
      if (month > 99) {
        setPeriodIsError(true);
        setPeriodErrorMessage(
          dashboardDes.myPlanCardDes.goalInformation.outPeriodErrorMessage
        );
      } else if (month < 0) {
        setPeriodIsError(true);
        setPeriod('');
      } else {
        setPeriodIsError(false);
      }
    };

    const validateDesc = desc => {
      if (desc === undefined || desc.length > 1000) {
        setDescIsError(true);
        setDescErrorMessage(
          dashboardDes.myPlanCardDes.goalInformation.outDescErrorMessage
        );
      } else if (desc.match(new RegExp(`[${inputBlackList}]`, 'g'))) {
        setDescIsError(true);
        setDescErrorMessage(`*Input should not contain ${inputBlackList}`);
      } else {
        setDescIsError(false);
      }
    };

    useEffect(() => {
      validatePeriod(period);
    }, [period]);

    useEffect(() => {
      validateDesc(description);
    }, [description]);

    useEffect(() => setIsDisplaySkillErrMsg && setIsDisplaySkillErrMsg(false), [
      setIsDisplaySkillErrMsg
    ]);

    return (
      <div className={styles.informationContainer}>
        <HexagonSkillView
          name={name}
          skillLevel={practiceLevel}
          isMyOwnGoal={isMyOwnGoal}
        />
        <div>
          <div className={styles.period}>
            <span>In&nbsp;</span>
            {justDisplay ? (
              <span>{period}</span>
            ) : (
              <InputWithStyle
                error={isPeriodError}
                width="80px"
                height="19px"
                type="number"
                placeholder={
                  dashboardDes.myPlanCardDes.goalInformation.titlePlaceholder
                }
                variant="outlined"
                value={period}
                onChange={event => setPeriod(event.target.value)}
                disabled={disabled}
                data-cy="durationInput"
              />
            )}
            <span>
              {!justDisplay && <i>&nbsp;*</i>}&nbsp;months, I would like to:
            </span>
          </div>
          <div className={clsx(styles.errorMessage, styles.periodError)}>
            {isPeriodError && periodErrorMessage}
          </div>
          {justDisplay ? (
            <div className={styles.displayDescription}>{description}</div>
          ) : (
            <InputWithStyle
              width="520px"
              height="57px"
              placeholder={
                dashboardDes.myPlanCardDes.goalInformation.textPlaceholder
              }
              disabled={disabled}
              variant="outlined"
              multiline
              minRows={3}
              value={description}
              onChange={event => setDescription(event.target.value)}
              error={isDescError}
              data-cy="descriptionInput"
            />
          )}
          {!justDisplay && <span className={styles.notificationDot}>*</span>}
          <div
            className={styles.errorMessage}
            data-cy="durationDescriptionErrorMsg"
          >
            {isDescError && descErrorMessage}
          </div>
        </div>
      </div>
    );
  }
);

GoalInformationForm.defaultProps = {
  justDisplay: false,
  disabled: false
};
GoalInformationForm.propTypes = {
  selectedSkill: PropTypes.shape({
    name: PropTypes.string,
    practiceLevel: PropTypes.number
  }),
  goal: PropTypes.shape({
    description: PropTypes.string,
    period: PropTypes.number,
    name: PropTypes.string,
    practiceLevel: PropTypes.number
  }),
  justDisplay: PropTypes.bool,
  disabled: PropTypes.bool,
  setIsDisplaySkillErrMsg: PropTypes.func,
  isMyOwnGoal: PropTypes.bool
};
export default GoalInformationForm;
